import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { getUserData, selectJwtToken } from "../../../redux/slices/authSlice";
import {
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Card,
  CardContent,
} from "@mui/material";
import { useFindByCustomerIdMutation } from "../../../redux/services/invoiceApi";
import {
  setInvoiceDetail,
  getInvoiceTotalPage,
  getInvoiceTotalRecord,
} from "../../../redux/slices/invoiceSlice";
import { formatDateTime, roundNumber } from "../../../redux/services/utility";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import MuiAlert from "../../MuiAlert/MuiAlert";
import Loading from "../../commons/Loading";
import bookingIcon from "../../../assets/MyBooking.png";
import paymentIcon from "../../../assets/PaymentDetail.png";
import pickupIcon from "../../../assets/date.svg";
import dropoffIcon from "../../../assets/date.svg";
import { BiChevronLeft, BiChevronRight } from "react-icons/bi";


const WalletHistory = () => {
  const dispatch: any = useAppDispatch();
  const [getWalletHistory, historyResponse] = useFindByCustomerIdMutation();
  const currentUser: any = JSON.parse(localStorage.getItem("user") || "{}");
  const token: string = currentUser?.jwtToken;
  const customerId: string = currentUser?.customerId;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [transactionDetail, setTransactionDetail] = useState<any>([]);
  const [paymentMethod, setPaymentMethod] = useState<string>("All");
  const totalTransactionRecords = useAppSelector(getInvoiceTotalRecord);
  const [statusCode, setStatusCode] = useState({ type: "", code: "" });
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [active, setActive] = useState("T");
  const [offset, setOffset] = useState(5);
  const [flagState, setFlagState] = useState("TRANSACTION");
  const totalPageRecord = useAppSelector(getInvoiceTotalPage);
  const [totalPages, setTotalPages] = useState(1);
  const [isOpen, setIsOpen] = useState(false);
  const [miscOpen, setMiscOpen] = useState(false);



  const fetchTransactionDetails = async () => {
    const payload: any = {
      customerId: customerId,
      offset: rowsPerPage,
      page: page,
      flag: "TRANSACTION",
      paymentMethod: paymentMethod,
    };
    await getWalletHistory({ token, payload });
  };

  useEffect(() => {
    fetchTransactionDetails();
  }, [page, rowsPerPage, paymentMethod]);

  useEffect(() => {
    if (historyResponse.isSuccess) {
      dispatch(setInvoiceDetail(historyResponse.data));
      setTransactionDetail(historyResponse.data.data);
    }
    if (historyResponse.isError) {
      setStatusCode({
        type: "error",
        code: "TECHNICAL_ERRORS",
      });
      setIsAlertVisible(true);
      setTimeout(() => setIsAlertVisible(false), 5000);
    }
  }, [historyResponse.isSuccess, historyResponse.isError]);



  const handleLoadMore = (dataFlag: string) => {
    setPage(page + 1);
    getTransactionDetailByPagination(offset, page + 1, dataFlag);
  };

  const handleLoadMores = (dataFlag: string) => {
    setPage(page - 1);
    getTransactionDetailByPagination(offset, page - 1, dataFlag);
  };


  const getTransactionDetailByPagination = async (
    reqOffset: number,
    reqPage: number,
    reqFlag: string
  ) => {
    await getWalletHistory({
      token: token,
      payload: {
        customerId: customerId,
        offset: reqOffset,
        page: reqPage,
        flag: reqFlag,
        paymentMethod: paymentMethod,
      },
    });
  };


  return (
    <div className="  m-[5px] md:m-[5px] md:mx-8 mx-2 p-[3px] md:p-[5px]">
      {isAlertVisible && (
        <MuiAlert
          severity={statusCode?.type as "error" | "info" | "success" | "warning"} // Cast type to valid AlertColor
          messageType={statusCode?.code!}
        />
      )}
      {historyResponse.isLoading ? (
        <Loading />
      ) : (
        <>
          <div className="flex md:flex-row flex-col  md:justify-between md:items-center items-start mb-3">
            <p className="text-[#434343] font-bold lg:text-[24px] md:text-[20px] text-[18px] md:mb-[20px] mb-[8px] ml-6">
              My Transactions
            </p>
            <FormControl fullWidth variant="outlined" className="max-w-[200px] md:mt-20 mt-8">
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={paymentMethod}
                onChange={(e) => {
                  setPaymentMethod(e.target.value);
                  setPage(0);
                  setOffset(5);
                }}
                label="Payment Method"
                className="text-gray-800"
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Cash">Cash</MenuItem>
                <MenuItem value="Online">Online</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div className="flex flex-col">
            {transactionDetail.length > 0 ? (
              transactionDetail.map((transaction: any, index: any) => (
                <div className={`rounded-3xl p-4 bg-[#F9F4EF] mt-2 mb-2`}>
                  <div className="flex flex-col gap-3">
                    <div className="flex justify-between items-center">
                      <p className="text-[#ED1C24] font-bold text-sm lg:text-lg">
                        {transaction.bookingCode}
                      </p>
                      <p className={` font-semibold text-sm md:text-base ${transaction.transactionType == 'Credit' ? 'text-green-500' : 'text-red-500'}`}>
                        {transaction.transactionType}
                      </p>
                      <span className="text-white bg-[#ED1C24] font-semibold text-sm uppercase px-3 py-1 rounded-full">
                        {transaction.invoiceStatus}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-gray-600 items-center">
                      <div className="flex items-center gap-2">
                        <img src={bookingIcon} alt="Booking Date" className="w-3 h-3 md:w-4 md:h-4 lg:w-5 lg:h-5" />
                        <p className="text-[#58595B] text-sm md:text-base">
                          {formatDateTime(transaction.bookingDate, "MM/DD/YYYY, hh:mm A")}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <img src={paymentIcon} alt="Payment Method" className="w-3 h-3 md:w-4 md:h-4 lg:w-5 lg:h-5" />
                        <p className="text-[#58595B] text-sm md:text-base">
                          {transaction.paymentMethodName}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <img src={pickupIcon} alt="Pickup Date" className="w-3 h-3 md:w-4 md:h-4 lg:w-5 lg:h-5" />
                        <p className="text-[#58595B] text-sm md:text-base">
                          {formatDateTime(transaction.checkInDate, "MM/DD/YYYY, hh:mm A")}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <img src={dropoffIcon} alt="Dropoff Date" className="w-3 h-3 md:w-4 md:h-4 lg:w-5 lg:h-5" />
                        <p className="text-[#58595B] text-sm md:text-base">
                          {formatDateTime(transaction.checkOutDate, "MM/DD/YYYY, hh:mm A")}
                        </p>
                      </div>
                    </div>
                    {(transaction.bookingStatus === "cancel" && transaction.transactionType == 'Credit') && (
                      <p className="text-red-500 font-semibold text-sm md:text-base">
                        Refund Amount {""}: ₹{roundNumber(transaction.cardRefundAmount)}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center">
                    <div className="flex-1">
                      <div className="text-md font-bold text-gray-600 mt-2">
                        Total Amount: ₹{transaction.invoiceAmount.toFixed(2)}
                      </div>

                      {(transaction.miscellaneousCharge?.length > 0 && !transaction.isMasterInvoice) && (
                        <div className="flex flex-col items-start">
                          <div
                            className="text-gray-600 text-sm flex items-center space-x-2 cursor-pointer mt-2"
                            onClick={() => setIsOpen(!isOpen)}
                          >
                            {isOpen ? (
                              <>
                                <span>Hide Breakup</span>
                                <ChevronUp size={16} className="bg-[#ED1C24] rounded-full text-white" />
                              </>
                            ) : (
                              <>
                                <span>View Breakup</span>
                                <ChevronDown size={16} className="bg-[#ED1C24] rounded-full text-white" />
                              </>
                            )}
                          </div>

                          {isOpen && (
                            <div className="mt-4 w-full">
                              <div className="flex flex-col justify-between">
                                <div
                                  className="flex justify-between py-1 cursor-pointer"
                                  onClick={() => setMiscOpen(!miscOpen)}
                                >
                                  <p className="text-base flex items-center text-start text-gray-600 ">
                                    Miscellaneous Charges
                                    {miscOpen ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                                  </p>
                                  <p className="text-base text-start text-gray-600">
                                    ₹{transaction?.miscellaneousCharge?.reduce((total: any, charge: any) => total + charge.amount, 0).toFixed(2)}
                                  </p>
                                </div>

                                {miscOpen && (
                                  <div className="pl-4">
                                    {transaction.miscellaneousCharge.map((charge: any) => (
                                      <div key={charge.id} className="flex justify-between py-1">
                                        <p className="text-base text-start text-gray-600">{charge.chargeName}</p>
                                        <p className="text-base text-start text-gray-600">₹{charge.amount.toFixed(2)}</p>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                <div className="flex justify-between py-1">
                                  <p className="text-base text-start text-gray-600">County Tax</p>
                                  <p className="text-base ml-5 text-start text-gray-600">₹{transaction?.extraCountyTax}</p>
                                </div>
                                <div className="flex justify-between py-1">
                                  <p className="text-base text-start text-gray-600">Sales Tax</p>
                                  <p className="text-base ml-5 text-start text-gray-600">₹{transaction?.extraSalesTax}</p>
                                </div>
                                <div className="flex justify-between py-1">
                                  <p className="text-base text-start text-gray-600">State Tax</p>
                                  <p className="text-base ml-5 text-start text-gray-600">₹{transaction?.extraStateTax}</p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {transaction.paymentMethodName !== "Cash" && (
                        <div className="text-md font-bold text-gray-600 mt-2">
                          Payment Status: {transaction.paymentStatus}
                        </div>
                      )}
                    </div>
                  </div>

                </div>
              ))
            ) : (
              <div className="text-center text-black py-6">No transactions available.</div>
            )}
            {transactionDetail.length > 0 && (
              <div className="flex gap-6 justify-center">
                <button
                  onClick={() => {
                    handleLoadMores(flagState);
                  }}
                  disabled={page == 0 ? true : false}
                  className={` p-2 rounded-[8px] flex items-center ${page == 0
                    ? "bg-[#58595B] text-white"
                    : "text-white bg-[#ED1C24]"
                    }`}
                >
                  <BiChevronLeft />
                </button>
                <button
                  onClick={() => {
                    handleLoadMore(flagState);
                  }}
                  disabled={page + 1 == totalPageRecord ? true : false}
                  className={` p-2 flex items-center rounded-[8px] ${page + 1 == totalPageRecord
                    ? "bg-[#58595B] text-white"
                    : "text-white bg-[#ED1C24]"
                    }`}
                >
                  <BiChevronRight />
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default WalletHistory;